<script setup>
/**
 * GviaPaymentMethodModal
 * Modal for selecting payment method:
 *   - Credit (exclusive, navigates to payment link)
 *   - Cash + Checks (combined, inline form)
 *
 * Two internal views:
 *   'select'     → Card selection (Credit vs Cash & Checks)
 *   'cashChecks' → Combined cash input + inline check rows
 */
import { ref, computed } from 'vue'
import { useGvia } from '@/core/composables/useGvia'
import { useBackButtonClose } from '@/core/composables/useBackButtonClose'

const props = defineProps({
  overrideAmount: { type: Number, default: 0 },
})

const emit = defineEmits(['close', 'select-credit', 'finish-payment'])

// Intercept back button to close modal instead of navigating away
const isOpen = ref(true)
useBackButtonClose(isOpen, handleBack)

const { paymentAmount, selectedPayments, formatCurrency } = useGvia()

// Manual mode when overrideAmount is provided
const isManualMode = computed(() => props.overrideAmount > 0)

// Effective amount: override takes precedence over invoice-based amount
const effectiveAmount = computed(() =>
  isManualMode.value ? props.overrideAmount : paymentAmount.value
)

// ── View management ──
const currentView = ref('select') // 'select' | 'cashChecks'

function handleBack() {
  if (currentView.value === 'cashChecks') {
    currentView.value = 'select'
  } else {
    emit('close')
  }
}

// ── Agent note (optional, travels with the payment everywhere) ──
const note = ref('')

// ── Cash state ──
// Israeli cash-payment cap: a single cash payment may not exceed ₪5,999.
const MAX_CASH_AMOUNT = 5999
const cashAmount = ref(0)

const maxCash = computed(() => Math.min(effectiveAmount.value, MAX_CASH_AMOUNT))
const isCashOverLimit = computed(() => (parseFloat(cashAmount.value) || 0) > MAX_CASH_AMOUNT)

// ── Checks state ──
const checks = ref([])

function createEmptyCheck() {
  return {
    id: globalThis.crypto.randomUUID(),
    checkNumber: '',
    date: '', // composed ISO yyyy-mm-dd (single source for validation/downstream)
    day: '',
    month: '',
    year: '',
    amount: null,
  }
}

function addCheck() {
  checks.value.push(createEmptyCheck())
}

function removeCheck(index) {
  if (checks.value.length <= 1) {
    // Reset the last check instead of removing
    checks.value = [createEmptyCheck()]
    touched.value.clear()
    return
  }
  checks.value.splice(index, 1)
}

// ── Check validation ──
function isCheckNumberValid(check) {
  return check.checkNumber.trim() !== '' && /^\d+$/.test(check.checkNumber.trim())
}

// Checks may be post-dated (future) or back-dated up to this many months.
const MAX_CHECK_MONTHS_BACK = 5

function isDateValid(check) {
  if (!check.date) return false
  const selected = new Date(check.date)
  const minDate = new Date()
  minDate.setHours(0, 0, 0, 0)
  minDate.setMonth(minDate.getMonth() - MAX_CHECK_MONTHS_BACK)
  selected.setHours(0, 0, 0, 0)
  return selected >= minDate
}

// ── Segmented date input (day / month / year) ──
// Native date pickers are slow for "random" future check dates in the field.
const DATE_SEGMENTS = ['day', 'month', 'year']

function composeCheckDate(check) {
  const d = parseInt(check.day, 10)
  const m = parseInt(check.month, 10)
  const y = parseInt(check.year, 10)
  if (!d || !m || !y || check.year.length !== 4) {
    check.date = ''
    return
  }
  // Reject impossible calendar dates (e.g. 31/02) via Date round-trip
  const dt = new Date(y, m - 1, d)
  check.date =
    dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
      ? `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      : ''
}

function onDateSegmentInput(check, segment, event) {
  const el = event.target
  const max = segment === 'year' ? 4 : 2
  const val = el.value.replace(/\D/g, '').slice(0, max)
  check[segment] = val
  el.value = val
  composeCheckDate(check)

  // Auto-advance: segment full, or a single digit that can't start a valid 2-digit value
  const complete =
    val.length === max ||
    (segment === 'day' && val.length === 1 && Number(val) > 3) ||
    (segment === 'month' && val.length === 1 && Number(val) > 1)

  if (complete) {
    const next = DATE_SEGMENTS[DATE_SEGMENTS.indexOf(segment) + 1]
    if (next) {
      el.closest('.date-segments')?.querySelector(`[data-seg="${next}"]`)?.focus()
    }
  }
}

function onDateSegmentKeydown(check, segment, event) {
  // Backspace on an empty segment jumps back to the previous one
  if (event.key === 'Backspace' && !event.target.value) {
    const prev = DATE_SEGMENTS[DATE_SEGMENTS.indexOf(segment) - 1]
    if (prev) {
      event.preventDefault()
      event.target.closest('.date-segments')?.querySelector(`[data-seg="${prev}"]`)?.focus()
    }
  }
}

function onDateGroupBlur(check, event) {
  // Only when focus leaves the whole group (not between segments)
  if (event.currentTarget.contains(event.relatedTarget)) return
  // Expand a 2-digit year shortcut ("26" → "2026")
  if (check.year.length === 2) {
    check.year = `20${check.year}`
    composeCheckDate(check)
  }
  markTouched(check.id, 'date')
}

function isAmountValid(check) {
  return check.amount !== null && check.amount > 0
}

function isCheckValid(check) {
  return isCheckNumberValid(check) && isDateValid(check) && isAmountValid(check)
}

// Track which fields have been touched (for showing errors)
const touched = ref(new Map())

function markTouched(checkId, field) {
  touched.value.set(`${checkId}_${field}`, true)
}

function isTouched(checkId, field) {
  return touched.value.get(`${checkId}_${field}`) || false
}

// ── Totals ──
const checksAmount = computed(() =>
  checks.value.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0)
)

const remaining = computed(() => {
  const cash = parseFloat(cashAmount.value) || 0
  return Number((effectiveAmount.value - cash - checksAmount.value).toFixed(2))
})

const isFullyAllocated = computed(() => remaining.value === 0 && effectiveAmount.value > 0)

// Check if any check row has data entered (incl. partially-typed dates)
function checkHasData(c) {
  return c.checkNumber.trim() || c.date || c.day || c.month || c.year || (c.amount && c.amount > 0)
}

const hasCheckData = computed(() => checks.value.some(checkHasData))

// All entered checks must be valid
const allChecksValid = computed(() => {
  if (!hasCheckData.value) return true
  return checks.value.filter(checkHasData).every(isCheckValid)
})

// Fill cash input with whatever remains after checks
function fillFullAmount() {
  const rem = Math.min(effectiveAmount.value - checksAmount.value, MAX_CASH_AMOUNT)
  cashAmount.value = rem > 0 ? Number(rem.toFixed(2)) : 0
}

// ── Open combined view ──
function openCashChecks() {
  if (checks.value.length === 0) {
    checks.value = [createEmptyCheck()]
  }
  currentView.value = 'cashChecks'
}

// ── Finish payment ──
function handleFinishPayment() {
  if (!isFullyAllocated.value || isCashOverLimit.value) return

  // Validate checks if any have data
  if (hasCheckData.value) {
    checks.value.forEach((c) => {
      markTouched(c.id, 'checkNumber')
      markTouched(c.id, 'date')
      markTouched(c.id, 'amount')
    })
    if (!allChecksValid.value) return
  }

  // Filter out empty check rows
  const validChecks = checks.value.filter((c) => c.checkNumber.trim() && c.date && c.amount > 0)

  emit('finish-payment', {
    cash: parseFloat(cashAmount.value) || 0,
    checks: validChecks.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0),
    checksDetails: validChecks.map((c) => ({ ...c })),
    isManual: isManualMode.value,
    amount: effectiveAmount.value,
    note: note.value.trim(),
  })
}

// Count of checks with data
const activeChecksCount = computed(() => checks.value.filter(checkHasData).length)
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="true" class="modal-overlay">
        <div class="modal-container">
          <!-- ════════════════════════════════════════ -->
          <!-- VIEW: Card Selection                     -->
          <!-- ════════════════════════════════════════ -->
          <template v-if="currentView === 'select'">
            <!-- Header -->
            <div class="modal-header">
              <h2 class="modal-title">בחר אמצעי תשלום</h2>
              <button class="close-btn" @click="emit('close')">
                <svg
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <!-- Amount Summary -->
            <div class="amount-summary">
              <span class="amount-label">סכום לגבייה</span>
              <span class="amount-value">{{ formatCurrency(effectiveAmount) }}</span>
              <span class="invoices-count">{{
                isManualMode ? 'לא נבחרו חשבוניות' : `${selectedPayments.length} חשבוניות נבחרו`
              }}</span>
            </div>

            <!-- Payment Options -->
            <div class="payment-options">
              <!-- Credit Card -->
              <button class="option-card" @click="emit('select-credit', note.trim())">
                <div class="option-icon credit-icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="48"
                    height="48"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <div class="option-content">
                  <h3 class="option-label">תשלום באשראי</h3>
                  <p class="option-desc">סליקה דרך קישור תשלום מאובטח</p>
                </div>
                <div class="option-arrow">
                  <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </div>
              </button>

              <!-- OR Divider -->
              <div class="or-divider">
                <span>או</span>
              </div>

              <!-- Combined Cash & Checks Card -->
              <button class="option-card cash-checks-card" @click="openCashChecks">
                <div class="option-icon cash-checks-icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="48"
                    height="48"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <circle cx="9" cy="9" r="7" />
                    <path d="M12.5 7h-1.2a1.5 1.5 0 000 3h0a1.5 1.5 0 010 3H10" />
                    <line x1="9" y1="5.5" x2="9" y2="7" />
                    <line x1="9" y1="13" x2="9" y2="14.5" />
                    <rect x="10" y="13" width="12" height="8" rx="1.5" />
                    <line x1="13" y1="17" x2="19" y2="17" />
                    <line x1="13" y1="19" x2="17" y2="19" />
                  </svg>
                </div>
                <div class="option-content">
                  <h3 class="option-label">תשלום מזומן ושיקים</h3>
                  <p class="option-desc">גביית תשלום במזומן, בשיקים, או שניהם</p>
                </div>
                <div class="option-arrow">
                  <svg
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </div>
              </button>
            </div>

            <!-- Agent Note -->
            <div class="note-section">
              <label class="note-label" for="gvia-agent-note">הערת סוכן (אופציונלי)</label>
              <textarea
                id="gvia-agent-note"
                v-model="note"
                class="note-input"
                rows="2"
                maxlength="300"
                placeholder="הערה שתופיע באישור התשלום ובמייל ללקוח"
              ></textarea>
            </div>
          </template>

          <!-- ════════════════════════════════════════ -->
          <!-- VIEW: Combined Cash & Checks Form        -->
          <!-- ════════════════════════════════════════ -->
          <template v-else>
            <!-- Header -->
            <div class="modal-header">
              <button class="back-btn" @click="currentView = 'select'">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <h2 class="modal-title">תשלום מזומן ושיקים</h2>
              <div class="header-amount-badge">
                {{ formatCurrency(effectiveAmount) }}
              </div>
            </div>

            <!-- Reactive remaining bar (top — footer gets hidden by the mobile keyboard) -->
            <div class="remaining-bar" :class="{ zero: remaining === 0 }">
              <span class="remaining-bar-label">יתרה:</span>
              <span class="remaining-bar-value">{{ formatCurrency(remaining) }}</span>
            </div>

            <!-- Scrollable content -->
            <div class="form-content">
              <!-- ── Cash Section ── -->
              <div class="form-section">
                <div class="section-header">
                  <div class="section-icon cash-section-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M16 8h-2a2 2 0 000 4h0a2 2 0 010 4H8" />
                      <line x1="12" y1="6" x2="12" y2="8" />
                      <line x1="12" y1="16" x2="12" y2="18" />
                    </svg>
                  </div>
                  <h3 class="section-title">מזומן</h3>
                </div>
                <div class="cash-input-row">
                  <input
                    v-model.number="cashAmount"
                    type="number"
                    class="cash-input"
                    :class="{ 'cash-input--error': isCashOverLimit }"
                    placeholder="הזן סכום"
                    min="0"
                    :max="maxCash"
                    step="0.01"
                    @focus="cashAmount === 0 && (cashAmount = null)"
                  />
                  <button class="fill-amount-btn" @click="fillFullAmount">כל הסכום</button>
                </div>
                <span class="cash-hint" :class="{ 'cash-hint--error': isCashOverLimit }">
                  {{
                    isCashOverLimit
                      ? 'סכום המזומן לא יכול לעלות על 5,999 ₪'
                      : 'ניתן לגבות עד 5,999 ₪ במזומן'
                  }}
                </span>
              </div>

              <!-- ── Divider ── -->
              <div class="section-divider"></div>

              <!-- ── Checks Section ── -->
              <div class="form-section">
                <div class="section-header">
                  <div class="section-icon checks-section-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <rect x="2" y="5" width="20" height="14" rx="2" />
                      <line x1="6" y1="12" x2="18" y2="12" />
                      <line x1="6" y1="15" x2="14" y2="15" />
                      <line x1="6" y1="9" x2="10" y2="9" />
                    </svg>
                  </div>
                  <h3 class="section-title">שיקים</h3>
                  <span v-if="activeChecksCount > 0" class="checks-count-badge">
                    {{ activeChecksCount }}
                    {{ activeChecksCount === 1 ? 'שיק' : 'שיקים' }}
                  </span>
                </div>

                <!-- Check Rows -->
                <div class="checks-list">
                  <div v-for="(check, index) in checks" :key="check.id" class="check-row">
                    <div class="check-row-header">
                      <span class="check-index">שיק {{ index + 1 }}</span>
                      <button
                        v-if="checks.length > 1"
                        class="remove-check-btn"
                        @click="removeCheck(index)"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                        </svg>
                      </button>
                    </div>

                    <div class="check-fields">
                      <!-- Check Number -->
                      <div class="field-group">
                        <label class="field-label">מספר שיק</label>
                        <input
                          v-model="check.checkNumber"
                          type="text"
                          inputmode="numeric"
                          class="field-input"
                          :class="{
                            'field-error':
                              isTouched(check.id, 'checkNumber') && !isCheckNumberValid(check),
                          }"
                          placeholder="הזן מספר"
                          @blur="markTouched(check.id, 'checkNumber')"
                        />
                        <span
                          v-if="isTouched(check.id, 'checkNumber') && !isCheckNumberValid(check)"
                          class="error-text"
                        >
                          מספר שיק נדרש (ספרות בלבד)
                        </span>
                      </div>

                      <!-- Payout Date (segmented day/month/year — faster than native picker) -->
                      <div class="field-group">
                        <label class="field-label">תאריך פרעון</label>
                        <div
                          class="date-segments"
                          :class="{
                            'field-error': isTouched(check.id, 'date') && !isDateValid(check),
                          }"
                          @focusout="onDateGroupBlur(check, $event)"
                        >
                          <input
                            type="text"
                            inputmode="numeric"
                            autocomplete="off"
                            class="date-seg"
                            data-seg="day"
                            placeholder="יום"
                            maxlength="2"
                            :value="check.day"
                            @input="onDateSegmentInput(check, 'day', $event)"
                            @keydown="onDateSegmentKeydown(check, 'day', $event)"
                          />
                          <span class="date-sep">/</span>
                          <input
                            type="text"
                            inputmode="numeric"
                            autocomplete="off"
                            class="date-seg"
                            data-seg="month"
                            placeholder="חודש"
                            maxlength="2"
                            :value="check.month"
                            @input="onDateSegmentInput(check, 'month', $event)"
                            @keydown="onDateSegmentKeydown(check, 'month', $event)"
                          />
                          <span class="date-sep">/</span>
                          <input
                            type="text"
                            inputmode="numeric"
                            autocomplete="off"
                            class="date-seg date-seg--year"
                            data-seg="year"
                            placeholder="שנה"
                            maxlength="4"
                            :value="check.year"
                            @input="onDateSegmentInput(check, 'year', $event)"
                            @keydown="onDateSegmentKeydown(check, 'year', $event)"
                          />
                        </div>
                        <span
                          v-if="isTouched(check.id, 'date') && !isDateValid(check)"
                          class="error-text"
                        >
                          תאריך פרעון תקין נדרש (עד 5 חודשים אחורה)
                        </span>
                      </div>

                      <!-- Amount -->
                      <div class="field-group">
                        <label class="field-label">סכום</label>
                        <input
                          v-model.number="check.amount"
                          type="number"
                          class="field-input amount-input"
                          :class="{
                            'field-error': isTouched(check.id, 'amount') && !isAmountValid(check),
                          }"
                          placeholder="₪ 0.00"
                          min="0"
                          step="0.01"
                          @blur="markTouched(check.id, 'amount')"
                          @focus="check.amount === 0 && (check.amount = null)"
                        />
                        <span
                          v-if="isTouched(check.id, 'amount') && !isAmountValid(check)"
                          class="error-text"
                        >
                          סכום חייב להיות גדול מ-0
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Add Check Button -->
                  <button class="add-check-btn" @click="addCheck">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    הוספת שיק נוסף
                  </button>
                </div>
              </div>

              <!-- ── Divider ── -->
              <div class="section-divider"></div>

              <!-- ── Agent Note ── -->
              <div class="note-section note-section--form">
                <label class="note-label" for="gvia-agent-note-form">הערת סוכן (אופציונלי)</label>
                <textarea
                  id="gvia-agent-note-form"
                  v-model="note"
                  class="note-input"
                  rows="2"
                  maxlength="300"
                  placeholder="הערה שתופיע באישור התשלום ובמייל ללקוח"
                ></textarea>
              </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer">
              <button
                class="finish-btn"
                :class="{ active: isFullyAllocated && allChecksValid && !isCashOverLimit }"
                :disabled="!isFullyAllocated || !allChecksValid || isCashOverLimit"
                @click="handleFinishPayment"
              >
                סיום תשלום
              </button>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--bg-primary);
  display: flex;
  align-items: stretch;
  justify-content: center;
  z-index: 9999;
}

.modal-container {
  background: var(--bg-primary);
  width: 100%;
  max-width: 900px;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
  gap: var(--spacing-md);
}

.modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
  flex: 1;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--bg-secondary);
  border: none;
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--bg-secondary);
  border: none;
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.back-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.header-amount-badge {
  padding: var(--spacing-xs) var(--spacing-md);
  background: var(--color-primary);
  color: white;
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  flex-shrink: 0;
}

/* Amount Summary */
.amount-summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-lg);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.amount-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.amount-value {
  font-size: var(--font-size-3xl, 2rem);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.invoices-count {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

/* Payment Options (select view) */
.payment-options {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
}

.option-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  text-align: right;
  font-family: var(--font-family);
  transition: all var(--transition-fast);
  width: 100%;
}

.option-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.option-card:active {
  transform: translateY(0);
}

.option-card:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.option-card:hover .credit-icon {
  background: rgba(59, 130, 246, 0.15);
  border-color: var(--color-info);
}

.option-card:hover .cash-checks-icon {
  background: rgba(16, 185, 129, 0.15);
  border-color: var(--color-success);
}

/* Icon */
.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: var(--border-radius-lg);
  flex-shrink: 0;
  transition: all var(--transition-fast);
}

.credit-icon {
  background: rgba(59, 130, 246, 0.08);
  color: var(--color-info);
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.cash-checks-icon {
  background: rgba(16, 185, 129, 0.08);
  color: var(--color-success);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

/* Content */
.option-content {
  flex: 1;
  min-width: 0;
}

.option-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.option-desc {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* Arrow */
.option-arrow {
  color: var(--text-muted);
  opacity: 0;
  transition: all var(--transition-fast);
}

.option-card:hover .option-arrow {
  opacity: 1;
}

/* Agent Note */
.note-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: 0 var(--spacing-lg) var(--spacing-lg);
}

.note-section--form {
  padding: 0;
}

.note-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.note-input {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-base);
  font-family: var(--font-family);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  color: var(--text-primary);
  resize: vertical;
  min-height: 56px;
  transition: border-color var(--transition-fast);
}

.note-input:focus {
  outline: none;
  border-color: var(--color-primary);
  background: var(--bg-primary);
}

/* OR Divider */
.or-divider {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin: var(--spacing-xs) 0;
}

.or-divider::before,
.or-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-color);
}

.or-divider span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  white-space: nowrap;
}

/* ═══════════════════════════════════════ */
/* Cash & Checks Form (cashChecks view)   */
/* ═══════════════════════════════════════ */

/* Reactive remaining bar — pinned under the header so the keyboard can't hide it */
.remaining-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-lg);
  background: rgba(239, 68, 68, 0.08);
  border-bottom: 1px solid rgba(239, 68, 68, 0.2);
  flex-shrink: 0;
  transition: background var(--transition-fast);
}

.remaining-bar-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.remaining-bar-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-error);
  direction: ltr;
  unicode-bidi: embed;
}

.remaining-bar.zero {
  background: rgba(16, 185, 129, 0.08);
  border-bottom-color: rgba(16, 185, 129, 0.2);
}

.remaining-bar.zero .remaining-bar-value {
  color: var(--color-success);
}

.form-content {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.section-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius);
  flex-shrink: 0;
}

.cash-section-icon {
  background: rgba(16, 185, 129, 0.1);
  color: var(--color-success);
}

.checks-section-icon {
  background: rgba(245, 158, 11, 0.1);
  color: var(--color-warning);
}

.section-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.checks-count-badge {
  padding: 2px var(--spacing-sm);
  background: var(--color-primary);
  color: white;
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  margin-inline-start: auto;
}

.section-divider {
  height: 1px;
  background: var(--border-color);
  margin: var(--spacing-xs) 0;
}

/* Cash Input */
.cash-input-row {
  display: flex;
  gap: var(--spacing-sm);
}

.cash-input {
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  color: var(--text-primary);
  text-align: center;
  font-family: var(--font-family);
  transition: border-color var(--transition-fast);
}

.cash-input:focus {
  outline: none;
  border-color: var(--color-success);
  background: var(--bg-primary);
}

.cash-input::placeholder {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
}

/* Hide number input spinners */
.cash-input::-webkit-outer-spin-button,
.cash-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.cash-input[type='number'] {
  -moz-appearance: textfield;
}

.fill-amount-btn {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-primary);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  white-space: nowrap;
  transition: all var(--transition-fast);
}

.fill-amount-btn:hover {
  background: var(--color-primary-hover);
}

.fill-amount-btn:active {
  transform: scale(0.97);
}

/* Cash over-limit state + hint */
.cash-input--error,
.cash-input--error:focus {
  border-color: var(--color-error);
}

.cash-hint {
  display: block;
  margin-top: var(--spacing-xs);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.cash-hint--error {
  color: var(--color-error);
  font-weight: var(--font-weight-medium);
}

/* Checks List */
.checks-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.check-row {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  transition: border-color var(--transition-fast);
}

.check-row:focus-within {
  border-color: var(--color-primary-light);
}

.check-row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-sm);
}

.check-index {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-warning);
}

.remove-check-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.remove-check-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
}

/* Fields */
.check-fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--spacing-sm);
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.field-input {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-base);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: var(--font-family);
  transition: border-color var(--transition-fast);
  width: 100%;
  box-sizing: border-box;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.field-input.field-error {
  border-color: var(--color-error);
}

.field-input.field-error:focus {
  border-color: var(--color-error);
}

/* Segmented date input — one bordered box, three quick numeric segments.
   LTR: a date reads like a number (dd/mm/yyyy), day leftmost. */
.date-segments {
  display: flex;
  align-items: center;
  direction: ltr;
  gap: 2px;
  padding: var(--spacing-sm) var(--spacing-sm);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  transition: border-color var(--transition-fast);
  box-sizing: border-box;
  width: 100%;
}

.date-segments:focus-within {
  border-color: var(--color-primary);
}

.date-segments.field-error {
  border-color: var(--color-error);
}

.date-seg {
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  color: var(--text-primary);
  text-align: center;
  direction: ltr;
}

.date-seg:focus {
  outline: none;
}

.date-seg:focus::placeholder {
  color: transparent;
}

.date-seg--year {
  flex: 1.5;
}

.date-seg::placeholder {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.date-sep {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
  user-select: none;
}

.amount-input {
  font-weight: var(--font-weight-semibold);
  text-align: center;
}

/* Hide number input spinners */
.amount-input::-webkit-outer-spin-button,
.amount-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.amount-input[type='number'] {
  -moz-appearance: textfield;
}

.error-text {
  font-size: var(--font-size-xs);
  color: var(--color-error);
}

/* Add Check Button */
.add-check-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: none;
  border: 2px dashed var(--border-color);
  border-radius: var(--border-radius-lg);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.add-check-btn:hover {
  border-color: var(--color-warning);
  color: var(--color-warning);
  background: rgba(245, 158, 11, 0.04);
}

/* Modal Footer */
.modal-footer {
  display: flex;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  flex-shrink: 0;
}

.finish-btn {
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--bg-tertiary);
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-muted);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: not-allowed;
  transition: all var(--transition-fast);
}

.finish-btn.active {
  background: var(--color-success);
  color: white;
  cursor: pointer;
}

.finish-btn.active:hover {
  background: #0ea572;
}

.finish-btn.active:active {
  transform: scale(0.97);
}

/* Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .option-icon {
    width: 48px;
    height: 48px;
  }

  .option-icon svg {
    width: 32px;
    height: 32px;
  }

  .option-arrow {
    opacity: 1;
  }

  .cash-input {
    font-size: var(--font-size-lg);
  }

  .modal-footer {
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .finish-btn {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: var(--font-size-sm);
  }

  .check-fields {
    grid-template-columns: 1fr;
  }

  .form-content {
    padding: var(--spacing-md);
  }
}
</style>
